'use client';

import { PortableText } from '@portabletext/react';
import { cn } from '@/lib/utils';
import Image from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';
import CheckList from './CheckList';

import scalesIcon from '@/public/text-only-crypto/chapter-icon-scales.svg';
import quoteMark from '@/public/text-only-crypto/quote-mark.svg';

/** Figma: desktop 7218:271, mobile 8662:24789. */
function Quote({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-[2rem] w-full lg:w-[86.25rem] lg:min-h-[11rem] px-[2.375rem] py-[1.75rem] bg-black/[0.04] border-l border-black backdrop-blur-[1.656rem]">
      <Image
        src={quoteMark}
        alt=""
        className="w-[2.75rem] h-[2.375rem] shrink-0"
      />
      <p className="mb-0 flex-1 min-w-0 font-geist font-medium max-lg:text-[2.75rem] lg:text-[3rem] leading-[1.31] max-lg:tracking-[-0.0825rem] lg:tracking-[-0.09rem] text-black">
        {text}
      </p>
    </div>
  );
}

export default function OriginSection({
  data,
  className,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
}) {
  const body =
    'mb-0 font-geist font-normal text-[2.25rem] leading-[1.4] tracking-[-0.0675rem] text-black opacity-80 whitespace-pre-wrap';

  return (
    <section
      id="chapter-7"
      data-chapter
      className={cn(
        'relative flex justify-center w-full bg-white px-[2rem] lg:px-0',
        className,
      )}
    >
      <div className="flex flex-col gap-[2.25rem] w-[min(94.5rem,100%)] overflow-clip rounded-[1rem] border border-black/10 bg-white max-lg:px-[2rem] max-lg:py-[3rem] lg:p-[5.25rem]">
        <div className="flex items-center justify-center w-[5.5rem] h-[5.875rem] bg-black/5">
          <Image src={scalesIcon} alt="" className="w-[3.5rem] h-[3.5rem]" />
        </div>

        <div className="flex flex-col gap-[3rem] w-full">
          <div className="flex flex-col gap-[2rem] w-full">
            {data?.originTitle && (
              <div
                className="richtext richtext-start max-lg:w-[min(38.5rem,100%)] lg:w-[58.5rem]
                prose-p:font-geist prose-p:font-medium max-lg:prose-p:text-[3.5rem] lg:prose-p:text-[4rem] prose-p:leading-[1.21] max-lg:prose-p:tracking-[-0.105rem] lg:prose-p:tracking-[-0.12rem] prose-p:text-black prose-p:mb-0"
              >
                <PortableText
                  value={data.originTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            <div className="flex flex-col gap-[2.5rem] w-full">
              {data?.originIntro && (
                <p
                  className={cn(
                    body,
                    'max-lg:w-[min(40.625rem,100%)] lg:w-[80.5rem]',
                  )}
                >
                  {data.originIntro}
                </p>
              )}
              {data?.originQuote1 && <Quote text={data.originQuote1} />}
            </div>
          </div>

          {(data?.originBridge || data?.originQuote2) && (
            <div className="flex flex-col gap-[2rem] w-full">
              {data?.originBridge && (
                <p className={cn(body, 'lg:w-[80.5rem]')}>{data.originBridge}</p>
              )}
              {data?.originQuote2 && <Quote text={data.originQuote2} />}
            </div>
          )}

          {data?.originAnswer && <p className={body}>{data.originAnswer}</p>}

          <CheckList
            items={data?.originItems}
            paddingClassName="p-[2.5rem]"
            panelClassName="bg-[#F4F4F4] border-black/[0.12] rounded-none lg:w-[86.25rem]"
            textClassName="text-black opacity-70 max-lg:leading-[normal]"
          />

          {data?.originOutro && (
            <div
              className="richtext richtext-start w-full
              prose-p:font-geist prose-p:font-normal prose-p:text-[2.25rem] prose-p:leading-[1.4] prose-p:tracking-[-0.0675rem] prose-p:text-black prose-p:opacity-80 prose-p:mb-[3rem] prose-p:whitespace-pre-wrap lg:prose-p:max-w-[69.25rem] lg:[&_p:last-child]:max-w-none"
            >
              <PortableText
                value={data.originOutro}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
